// ---------------------------------------------------------------------------
// AgentForm.reducer — state transitions for the agent create/edit form. All
// scalar fields are edited through SET_FIELD (every non-`variables` field of
// AgentFormState is a string), while the variable row list has dedicated
// add/update/remove actions. Pure: no React, no side effects.
// ---------------------------------------------------------------------------

import type { AgentFormState, VariableRow } from './AgentForm.types.js';
import { emptyVariableRow } from './AgentForm.utils.js';

/** Every field of AgentFormState except the `variables` row list is a string. */
export type AgentFormStringField = Exclude<keyof AgentFormState, 'variables'>;

export type AgentFormAction =
  | { type: 'SET_FIELD'; key: AgentFormStringField; value: string }
  | { type: 'ADD_VARIABLE' }
  | { type: 'DECLARE_VARIABLE'; name: string }
  | { type: 'UPDATE_VARIABLE'; id: string; patch: Partial<VariableRow> }
  | { type: 'REMOVE_VARIABLE'; id: string };

export function agentFormReducer(
  state: AgentFormState,
  action: AgentFormAction,
): AgentFormState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.key]: action.value };
    case 'ADD_VARIABLE':
      return { ...state, variables: [...state.variables, emptyVariableRow()] };
    case 'DECLARE_VARIABLE':
      return {
        ...state,
        variables: [
          ...state.variables,
          { ...emptyVariableRow(), name: action.name },
        ],
      };
    case 'UPDATE_VARIABLE':
      return {
        ...state,
        variables: state.variables.map((v) =>
          v.id === action.id ? { ...v, ...action.patch } : v,
        ),
      };
    case 'REMOVE_VARIABLE':
      return {
        ...state,
        variables: state.variables.filter((v) => v.id !== action.id),
      };
    default:
      return state;
  }
}
